import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { adminApi } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  MessageSquare, Loader2, Send, Package, User, Clock, 
  ExternalLink, AlertTriangle, CheckCircle, XCircle, Eye, Trash2, RefreshCcw
} from 'lucide-react';

const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { 
  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
});
const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { 
  style: 'currency', currency: 'NGN', minimumFractionDigits: 0 
}).format(amount);

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchDisputes(); }, []);
  useEffect(() => { 
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); 
  }, [selectedDispute?.messages]);

  const fetchDisputes = async () => {
    try { 
      const response = await api.get('/admin/disputes'); 
      setDisputes(response.data); 
    } catch (error) { 
      console.error('Failed to fetch disputes:', error); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleDeleteDispute = async (disputeId) => {
    if (!window.confirm('Are you sure you want to delete this dispute?')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteDispute(disputeId);
      toast.success('Dispute deleted');
      setSelectedDispute(null);
      fetchDisputes();
    } catch (error) {
      toast.error('Failed to delete dispute');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllDisputes = async () => {
    if (!window.confirm('Are you sure you want to delete ALL disputes? This cannot be undone.')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteAllDisputes();
      toast.success('All disputes deleted');
      setSelectedDispute(null);
      fetchDisputes();
    } catch (error) {
      toast.error('Failed to delete disputes');
    } finally {
      setDeleting(false);
    }
  };

  const handleRefund = async (disputeId) => {
    if (!window.confirm('Are you sure you want to refund this order? This will:\n• Credit the order amount back to user\'s wallet\n• Delete the license keys from the user\'s records\n• Mark the dispute as resolved')) return;
    
    setRefunding(true);
    try {
      const response = await adminApi.refundDispute(disputeId);
      toast.success(`Refund successful: ₦${response.data.refund_amount?.toLocaleString() || 0} credited to user`);
      fetchDisputeDetails(disputeId);
      fetchDisputes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefunding(false);
    }
  };

  const fetchDisputeDetails = async (disputeId) => {
    try {
      const response = await api.get(`/admin/disputes/${disputeId}`);
      setSelectedDispute(response.data);
    } catch (error) {
      toast.error('Failed to load dispute details');
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/admin/orders/${orderId}`);
      setOrderDetails(response.data);
      setShowOrderDialog(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleStatusChange = async (disputeId, status) => {
    try { 
      await api.post(`/admin/disputes/${disputeId}/status`, { status }); 
      toast.success('Status updated'); 
      fetchDisputes();
      if (selectedDispute?.id === disputeId) {
        setSelectedDispute(prev => ({ ...prev, status }));
      }
    } catch (error) { 
      toast.error('Failed to update status'); 
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedDispute) return;
    
    setSending(true);
    try {
      await api.post(`/admin/disputes/${selectedDispute.id}/messages`, { content: message });
      setMessage('');
      fetchDisputeDetails(selectedDispute.id);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">
          Disputes ({disputes.length})
        </h1>
        <div className="flex items-center gap-2">
          <Badge className="bg-yellow-500/20 text-yellow-500">
            {disputes.filter(d => d.status === 'open').length} Open
          </Badge>
          <Badge className="bg-green-500/20 text-green-500">
            {disputes.filter(d => d.status === 'resolved').length} Resolved
          </Badge>
          {disputes.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteAllDisputes}
              disabled={deleting}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10 ml-2"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete All
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Disputes List */}
        <div className="lg:col-span-1 space-y-3">
          {disputes.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">No disputes yet</p>
              </CardContent>
            </Card>
          ) : (
            disputes.map((dispute) => (
              <Card 
                key={dispute.id} 
                className={`bg-[#0A0A0A] border-2 cursor-pointer transition-all hover:border-[#FFD700]/50 ${
                  selectedDispute?.id === dispute.id ? 'border-[#FFD700]' : 'border-white/10'
                }`}
                onClick={() => fetchDisputeDetails(dispute.id)}
                data-testid={`dispute-card-${dispute.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      dispute.status === 'open' ? 'bg-yellow-500/20' : 
                      dispute.status === 'resolved' ? 'bg-green-500/20' : 'bg-zinc-500/20'
                    }`}>
                      {dispute.status === 'open' ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      ) : dispute.status === 'resolved' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">{dispute.title}</p>
                      <p className="text-sm text-zinc-500">
                        <User className="w-3 h-3 inline mr-1" />{dispute.username}
                      </p>
                      <p className="text-xs text-zinc-600 mt-1">
                        Order #{dispute.order_id?.slice(0, 8)} · {formatDate(dispute.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Chat Panel */}
        <div className="lg:col-span-2">
          {selectedDispute ? (
            <Card className="bg-[#0A0A0A] border-white/10 h-[600px] flex flex-col">
              {/* Header */}
              <CardHeader className="border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#FFD700]" />
                      {selectedDispute.title}
                    </CardTitle>
                    <p className="text-sm text-zinc-500 mt-1">
                      Opened by {selectedDispute.username} · {formatDate(selectedDispute.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchOrderDetails(selectedDispute.order_id)}
                      className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                      data-testid="view-order-btn"
                    >
                      <Eye className="w-4 h-4 mr-1" />View Order
                    </Button>
                    {selectedDispute.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRefund(selectedDispute.id)}
                        disabled={refunding}
                        className="border-green-500/30 text-green-500 hover:bg-green-500/10"
                        data-testid="refund-dispute-btn"
                      >
                        {refunding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                        Refund
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteDispute(selectedDispute.id)}
                      disabled={deleting}
                      className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Select 
                      value={selectedDispute.status} 
                      onValueChange={(v) => handleStatusChange(selectedDispute.id, v)}
                    >
                      <SelectTrigger className="w-32 bg-[#121212] border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>

              {/* Description */}
              {selectedDispute.description && (
                <div className="p-4 bg-[#121212] border-b border-white/10">
                  <p className="text-sm text-zinc-400">
                    <span className="font-semibold text-white">Issue Description:</span><br />
                    {selectedDispute.description}
                  </p>
                </div>
              )}

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedDispute.messages?.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No messages yet. Start the conversation.</p>
                ) : (
                  selectedDispute.messages?.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-lg p-3 ${
                        msg.is_admin 
                          ? 'bg-[#FFD700]/20 border border-[#FFD700]/30' 
                          : 'bg-[#121212]'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            msg.is_admin ? 'text-[#FFD700]' : 'text-zinc-400'
                          }`}>
                            {msg.is_admin ? 'Admin' : msg.username || selectedDispute.username}
                          </span>
                          <span className="text-xs text-zinc-500">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-white text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Message Input */}
              {selectedDispute.status === 'open' && (
                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10 flex gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="bg-[#121212] border-white/10 text-white"
                    data-testid="dispute-message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !message.trim()} 
                    className="bg-[#FFD700] text-black hover:bg-[#E5C100]"
                    data-testid="send-dispute-message-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              )}

              {selectedDispute.status !== 'open' && (
                <div className="p-4 border-t border-white/10 bg-[#121212]">
                  <p className="text-center text-zinc-500">
                    This dispute is {selectedDispute.status}. Reopen to continue the conversation.
                  </p>
                </div>
              )}
            </Card>
          ) : (
            <Card className="bg-[#0A0A0A] border-white/10 h-[600px] flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">Select a dispute to view details and chat</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-bold text-xl flex items-center gap-2">
              <Package className="w-5 h-5 text-[#FFD700]" />
              Order #{orderDetails?.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          {orderDetails && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#121212] rounded-lg">
                <div>
                  <p className="text-zinc-500 text-xs">Customer</p>
                  <p className="text-white font-semibold">{orderDetails.username}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Status</p>
                  <Badge className={orderDetails.status === 'completed' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}>
                    {orderDetails.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Date</p>
                  <p className="text-white">{formatDate(orderDetails.created_at)}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Total</p>
                  <p className="text-[#FFD700] font-bold">{formatMoney(orderDetails.total_amount)}</p>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold">Order Items</h3>
                {orderDetails.items?.map((item, idx) => (
                  <div key={idx} className="p-4 bg-[#121212] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-semibold">{item.title}</p>
                      <p className="text-[#FFD700]">{formatMoney(item.subtotal)}</p>
                    </div>
                    <p className="text-sm text-zinc-500">Qty: {item.quantity} × {formatMoney(item.price)}</p>
                    
                    {/* License Keys */}
                    {item.licenses?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-zinc-400 font-semibold">License Keys Delivered:</p>
                        {item.licenses.map((license, i) => (
                          <div key={i} className="bg-black/50 rounded p-2">
                            <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{license}</pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Link to={`/admin/orders`} className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                    <ExternalLink className="w-4 h-4 mr-2" />Go to Orders
                  </Button>
                </Link>
                <Button 
                  onClick={() => setShowOrderDialog(false)}
                  className="flex-1 bg-[#FFD700] text-black hover:bg-[#E5C100]"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
